import { FormDateTime, FormInput, FormState, Labeled } from 'component_utils/FormUtils'
import VirtualizedList from 'components/BeTable/VirtualizedList'
import ProcessingButton from 'components/ProcessingButton'
import RoleProtected from 'components/RoleProtected'
import useResizeDetector from 'components/useResizeDetector'
import { ISimpleUserDetails, IUserTask, IUserTask$ITimesWorked } from 'interfaces'
import React, { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FaClock, FaEdit, FaFlagCheckered, FaShareAlt, FaSync, FaTrash } from 'react-icons/fa'
import { Button, ButtonGroup, Card, CardBody, CardFooter, CardHeader, Collapse, Input, Table } from 'reactstrap'
import { authToSimpleUser, useApi, useApiLoader, useAuth } from 'utils/API'
import Translation from 'utils/Language/Translation'
import { confirm, prompt } from 'utils/Prompts'
import { isNil, sortBy, sumBy } from 'lodash'
import ExpandedIndicator from 'components/ExpandedIndicator'
import If from 'components/If'
import Raw from 'components/Raw'
import Date, { DATE_TIME } from 'components/Date'
import TaskInitializer from './TaskInitializer'
import SimpleModal from 'components/SimpleModal'
import moment from 'moment'
import { BiCabinet } from 'react-icons/bi'
import { beTableAsComponent } from 'components/BeTable'
import { mapNotNil, retryForOptimisticLocking } from 'component_utils/utils'
import TimerImage from '../../../stopwatch.gif';
import classNames from 'classnames'
import { useSpecializations } from 'utils/SpecializationProvider'

const OverviewTable = beTableAsComponent<IUserTask & { usernames: string[] }, { reload: any; users: ISimpleUserDetails[] }>({
  tableId: 'table#user_task_overview',
  listLayoutBelow: 'sm',
  columns: [
    {
      Header: 'ID',
      value: row => row.id
    },
    {
      Header: 'Created at',
      value: row => row.createdAt,
      Cell: ({ row }) => <Date format={DATE_TIME} val={row.createdAt} />,
      sortable: true,
      filterable: true,
    },
    {
      Header: 'Finished at',
      value: row => row.finishedAt,
      Cell: ({ row }) => row.finishedAt && <Date format={DATE_TIME} val={row.finishedAt} />,
      sortable: true,
      filterable: true,
    },
    {
      Header: 'Users',
      value: row => row.usernames.join(", "),
      filterable: true,
      fullText: true,
    },
    {
      Header: 'Title',
      value: row => row.title,
      filterable: true,
    },
    {
      Header: 'Time',
      Cell: ({ row, reload, users }) => {
        const minutes = sumBy(row.timesWorked, tw => (
          moment.duration(
            (tw.finished ? moment(tw.finished) : moment()).diff(moment(tw.started))
          ).asMinutes()
        ))

        return (
          <>
            <TimeView entry={row} reload={reload} onlyMine={false} users={users} />
            {' '}
            {
              !isNaN(minutes) &&
              minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
            }
          </>
        )
      }
    },
    {
      Header: '',
      width: 60,
      fixedWidth: true,
      resizable: false,
      Cell: ({ row, reload }) => {
        const api = useApi()
        return (
          <ProcessingButton block color='danger' className='py-0' onClick={async () => {
            if (await confirm(<Translation name="T.misc.areYouSure?"/>)) {
              await api.userTasks.deleteUserTask(row.id)
              reload()
            }
          }}>
            <FaTrash/>
          </ProcessingButton>
        )
      }
    },
  ]
})

const Overview: FC<{ users: ISimpleUserDetails[] }> = ({ users }) => {
  const api = useApi()
  const { data, isLoading, reload } = useApiLoader(
    useCallback(async () => {
      const d = await api.userTasks.getAllTasks()

      return d.map(it => ({
        ...it,
        usernames: mapNotNil(it.users, (uc) => {
          return users.find(it => it.userid === uc)?.username
        })
      }))
    }, [api, users]),
    [],
    false
  )
  return (
    <SimpleModal
      title=''
      fullscreen
      onOpen={reload}
      trigger={open => (
        <Button onClick={open}>
          <BiCabinet/>
        </Button>
      )}
    >
      <OverviewTable
        loading={isLoading}
        data={data}
        getExtraProps={() => ({
          reload,
          users
        })}
      />
    </SimpleModal>
  )
}

const TimeViewEditor: FC<{ user: ISimpleUserDetails; trigger: any; getBase: () => IUserTask$ITimesWorked; save: any; }> = ({ user, trigger, getBase, save }) => {
  const [state, setState] = useState({})
  return (
    <SimpleModal
      title=""
      trigger={trigger}
      onOpen={() => {
        const base = getBase()
        setState({
          ...base, 
          started: base.started ? moment(base.started).toDate() : null,
          finished: base.finished ? moment(base.finished).toDate() : null,
        })
      }}
      buttons={close => [
        <ProcessingButton color='primary' onClick={async () => {
          await save(state)
          close()
        }}>
          <Translation name="T.misc.save"/>
        </ProcessingButton>
      ]}
    >
      <FormState state={state} setState={setState}>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.message"/>}>
          <FormInput path='message'/>
        </Labeled>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.started"/>}>
          <FormDateTime path='started'/>
        </Labeled>
        <Labeled title={<Translation name="T.objects.userTasks.workedTimes.finished"/>}>
          <FormDateTime path='finished' allowClear/>
        </Labeled>
      </FormState>
    </SimpleModal>
  )
}

const TimeView: FC<{ entry: IUserTask, reload: any; onlyMine: boolean; users: ISimpleUserDetails[] }> = ({ entry, reload, onlyMine, users }) => {
  const api = useApi()
  const auth = useAuth()

  const currentUserWorkEntry = entry.timesWorked.find(it => it.userId === auth.userId && it.finished === null)

  const showEntries = onlyMine ? entry.timesWorked.filter(it => it.userId === auth.userId) : entry.timesWorked

  return (
    <SimpleModal
      title={<Translation name="T.objects.userTasks.workedTimes.workedTimesFor" params={(entry || {}) as any}/>}
      trigger={open => (
        <ProcessingButton className='py-0 mb-2' onClick={open}>
          <FaClock/>
        </ProcessingButton>
      )}
      buttons={close => [
        <TimeViewEditor
          user={authToSimpleUser(auth)}
          trigger={(open: any) => (
            <ProcessingButton onClick={open}>
              <Translation name='T.misc.new'/>
            </ProcessingButton>  
          )}
          getBase={() => ({
            id: null,
            version: 0,
            userId: auth.userId,
            message: '',
            started: null,
            finished: null,
          })}
          save={async (tw: IUserTask$ITimesWorked) => {
            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.push(tw)
              await api.userTasks.saveUserTask(currEntry)
            })
          }}
        />,
        currentUserWorkEntry ? (
          <ProcessingButton color='danger' onClick={async () => {
            const msg = await prompt(<Translation name="T.objects.userTasks.workedTimes.describeWhatWorkYouDid"/>, '', true)
            if (msg === null) {
              return;
            }

            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.forEach(it => {
                if (it.userId === auth.userId && it.finished === null) {
                  it.finished = moment().toDate()
                  it.message = msg
                }
              })
              await api.userTasks.saveUserTask(currEntry)
            })
            reload()
          }}>
            <Translation name="T.misc.finish"/>
          </ProcessingButton>
        ) : (
          <ProcessingButton color='success' onClick={async () => {
            await retryForOptimisticLocking(3, async () => {
              const currEntry = await api.userTasks.getTask(entry.id)
              currEntry.timesWorked.push({
                id: null,
                version: 0,
                userId: auth.userId,
                message: '',
                started: moment().toDate(),
                finished: null
              })
              await api.userTasks.saveUserTask(currEntry)
            })
            reload()
          }}>
            <Translation name="T.misc.start"/>
          </ProcessingButton>
        )
      ]}
    >
      <Table striped>
        <tbody>
          {showEntries.map(tw => {
            let minutes = null;
            minutes = moment.duration(
              (tw.finished ? moment(tw.finished) : moment()).diff(moment(tw.started))
            ).asMinutes();
            return (
              <tr key={tw.id}>
                <td className='w-100'>
                  {!onlyMine && (
                    <b>
                      {users.find(it => it.userid === tw.userId)?.username ?? ""}:{' '}
                    </b>
                  )}
                  {tw.message && <><b>{tw.message}</b><br/></>}
                  Start: <Date val={tw.started} format={DATE_TIME}/><br/>
                  Stopped: {tw.finished && <Date val={tw.finished} format={DATE_TIME}/>}<br/>
                  Minutes: {
                    !isNaN(minutes) &&
                    minutes !== null && <Translation name="T.misc.minutes" params={{ minutes: minutes?.toFixed(0) }} />
                  }<br/>
                </td>
                <td>
                  <TimeViewEditor
                    user={users.find(it => it.userid === tw.userId)}
                    trigger={(open: any) => (
                      <ProcessingButton className='mb-2 py-0' onClick={open}>
                        <FaEdit/>
                      </ProcessingButton>  
                    )}
                    getBase={() => tw}
                    save={async (tw: IUserTask$ITimesWorked) => {
                      await retryForOptimisticLocking(3, async () => {
                        const currEntry = await api.userTasks.getTask(entry.id)
                        const idx = currEntry.timesWorked.findIndex(it => it.id === tw.id)
                        currEntry.timesWorked[idx] = tw
                        await api.userTasks.saveUserTask(currEntry)
                      })
                    }}
                  />
                  <br/>
                  <ProcessingButton color='danger' className='py-0' onClick={async () => {
                    if (!(await confirm(<Translation name="T.misc.areYouSure?"/>))) {
                      return
                    }

                    await retryForOptimisticLocking(3, async () => {
                      const currEntry = await api.userTasks.getTask(entry.id)
                      currEntry.timesWorked = currEntry.timesWorked.filter(it => it.id !== tw.id)
                      await api.userTasks.saveUserTask(currEntry)
                    })
                    reload()                    
                  }}>
                    <FaTrash/>
                  </ProcessingButton>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </SimpleModal>
  )
}

const Entry: FC<{ entry: IUserTask, reload: any }> = ({ entry, reload }) => {
  const [isOpen, setIsOpen] = useState(false)
  const api = useApi()
  const auth = useAuth()
  const specialization = useSpecializations()

  const currentUserWorkEntry = entry.timesWorked.find(it => it.userId === auth.userId && it.finished === null)

  const footerElement = specialization?.augmentations?.userTasks?.taskFooter?.(auth, entry)

  const hasContent = entry.text || footerElement

  return (
    <>
      <Card outline style={{
        borderColor: entry.borderColor ? entry.borderColor : undefined
      }}>
        <CardHeader className='d-flex flex-row align-items-center' onClick={() => setIsOpen(old => hasContent && !old)}>
          {hasContent && <ExpandedIndicator isExpanded={isOpen}/>}
          {entry.users.length > 1 && (
            <FaShareAlt className='mr-1'/>
          )}
          {currentUserWorkEntry && (
            <img alt="timer indicator" className={classNames('mr-1 react-icons')} style={{ width: 'unset' }} src={TimerImage} />
          )}
          <div className="flex-fill">
            {entry.title}<br/>
            <Date val={entry.createdAt} format={DATE_TIME}/>
          </div>
          <div className='mr-1' onClick={e => {
            e.stopPropagation()
          }}>
            <TimeView entry={entry} reload={reload} onlyMine users={[]}/>
            <br/>
            {entry.users.length > 1 && (
              <>
                <ProcessingButton disabled={!isNil(entry.claimedByUser)} className='py-0 mb-2' onClick={async () => {
                  await api.userTasks.claimSharedTask(entry.id)
                  reload()
                }}>
                  <FaShareAlt/>
                </ProcessingButton>
                <br/>
              </>
            )}
            <ProcessingButton className='py-0' onClick={async () => {
              if (await confirm(<Translation name="T.misc.areYouSure?"/>)) {
                await api.userTasks.markFinished(entry.id)
              }
            }}>
              <FaFlagCheckered/>
            </ProcessingButton>
          </div>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <If cond={entry.text}>
            <CardBody>
              <Raw>{entry.text.replaceAll("\n", "<br/>")}</Raw>
            </CardBody>
          </If>
          <If cond={footerElement}>
            <CardFooter className='text-right'>
              {footerElement ? footerElement : null}
            </CardFooter>
          </If>
        </Collapse>
      </Card>
    </>
  )
}

const listeners: any[] = []
export let refreshCurrentUserTasks = () => {
  listeners.forEach(it => it())
}

export default () => {
  const { data: users, lazyLoad: lazyLoadUsers } = useApiLoader(useCallback(api => api.users.getSimpleUsers(), []), [], false)
  const auth = useAuth()
  const { data, reload } = useApiLoader(
    useCallback(
      async api => sortBy(await api.userTasks.getCurrentTasks(), it => it.priority * -1), []
    ),
    [],
    true
  )
  const { height, ref: sizeRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: false,
    refreshMode: 'debounce',
    refreshRate: 500,
  });

  useEffect(() => {
    listeners.push(reload)
    return () => {
      listeners.splice(listeners.indexOf(reload), 1);
    }
  }, [reload])

  const [filterState, setFilterState] = useState({
    title: '',
    activeOnly: true
  })

  const filteredTasks = useMemo(() => {
    let res = data
    res = res.filter(it => it.claimedByUser === null || it.claimedByUser === auth.userId)
    if (filterState.activeOnly) {
      res = res.filter(it => it.finishedAt === null)
    }
    if (filterState.title.trim()) {
      res = res.filter(it => it.title.includes(filterState.title.trim()))
    }
    return sortBy(res, it => -it.priority)
  }, [data, filterState, auth.userId])

  return (
    <div className='flex-container'>
      <Input type="text" value={filterState.title} onChange={e => {
        const v = e.target.value
        setFilterState(old => ({...old, title: v}))
      }}/>
      <div className={'nested-flex-container my-2'} ref={sizeRef}>
        <VirtualizedList
          containerProps={{
            style: {
              overflowY: 'scroll',
              height: `${height}px`,
              width: '100%',
            } as CSSProperties,
          }}
          padRenderer={({ height }) => (
            <div
              style={{
                width: `100%`,
                height: `${height}px`,
                ...(height === 0 ? { display: 'none' } : {}),
              }}
            />
          )}
          maxHeight={height}
          overscan={0}
          minRowSize={30}
          rowCount={filteredTasks.length}
          rowRenderer={({ index, props }) => {
            const entry = filteredTasks[index];
            return (
              <div key={index} {...props} className='pb-2'>
                <Entry reload={reload} entry={entry}/>
              </div>
            );
          }}
        />
      </div>
      <ButtonGroup className='w-100'>
        <RoleProtected roles={['userTaskManager']} onHasRole={useCallback(() => {
          lazyLoadUsers()
        }, [lazyLoadUsers])}>
          <TaskInitializer
            users={users}
            reloadTasks={reload}
            trigger={(open: any) => (
              <ProcessingButton
                block 
                color='primary'
                onClick={async () => {
                  open({
                    id: -1,
                    createdAt: null,
                    updatedAt: null,
              
                    version: 0,
              
                    priority: 0,
                    borderColor: '',
                    title: '',
                    text: '',
              
                    tags: [],
                    users: []
                  })
                }}
              >
                <Translation name="T.misc.new"/>
              </ProcessingButton>      
            )}      
          />
          <Overview users={users}/>
        </RoleProtected>
        <ProcessingButton color='info' onClick={reload}>
          <FaSync/>
        </ProcessingButton>
      </ButtonGroup>
    </div>
  )
}